import { useState } from 'react';
import { createPortal } from 'react-dom';
import { C } from '../styles/tokens.js';

function fmtTs(ts) {
  const d = new Date(ts);
  const now = new Date();
  const sameDay = d.toDateString() === now.toDateString();
  const time = `${String(d.getHours()).padStart(2,'0')}:${String(d.getMinutes()).padStart(2,'0')}`;
  if (sameDay) return `오늘 ${time}`;
  const yest = new Date(now); yest.setDate(yest.getDate() - 1);
  if (d.toDateString() === yest.toDateString()) return `어제 ${time}`;
  return `${d.getMonth()+1}/${d.getDate()} ${time}`;
}

function projectsSummary(projects) {
  const totalCards = projects.reduce((s, p) => s + (p.data?.cards?.length || 0), 0);
  const totalAreas = projects.reduce((s, p) => s + (p.data?.areas?.length || 0), 0);
  const totalDates = projects.reduce((s, p) => s + (p.data?.dates?.length || 0), 0);
  return `${projects.length}개 프로젝트 · 카드 ${totalCards} · 영역 ${totalAreas} · 마감일 ${totalDates}`;
}

export default function SnapshotsModal({ snapshots, takeSnapshot, restoreSnapshot, deleteSnapshot, clearAllSnapshots, exportBackup, importBackup, onClose }) {
  const [toast, setToast] = useState(null);
  const show = (m) => { setToast(m); setTimeout(() => setToast(null), 2500); };

  return createPortal(
    <div onClick={onClose} onMouseDown={e => e.stopPropagation()}
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.8)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 99999, padding: 20 }}>
      <div onClick={e => e.stopPropagation()} onMouseDown={e => e.stopPropagation()}
        style={{ background: C.surface, borderRadius: 14, width: '100%', maxWidth: 640, maxHeight: '85vh', display: 'flex', flexDirection: 'column', border: `1px solid ${C.border}`, boxShadow: '0 24px 64px rgba(0,0,0,0.6)' }}>
        <div style={{ padding: '20px 24px', borderBottom: `1px solid ${C.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <div style={{ fontSize: 17, fontWeight: 800, color: C.text }}>🕐 시간별 복원 (자동 백업)</div>
            <div style={{ fontSize: 11, color: C.dim, marginTop: 4 }}>2분마다 자동 스냅샷 · 최대 20개 보관 · 모두 localStorage에 안전 저장</div>
          </div>
          <button onClick={onClose} style={{ background: 'transparent', border: 'none', color: C.dim, fontSize: 22, cursor: 'pointer' }}>×</button>
        </div>

        <div style={{ padding: '14px 24px', display: 'flex', gap: 6, borderBottom: `1px solid ${C.border}`, flexWrap: 'wrap' }}>
          <button onClick={() => { takeSnapshot('수동'); show('지금 스냅샷 저장됨'); }}
            style={{ padding: '7px 14px', borderRadius: 6, border: 'none', background: C.gold, color: '#000', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>📸 지금 스냅샷</button>
          <button onClick={() => { exportBackup(); show('JSON 백업 다운로드'); }}
            style={{ padding: '7px 14px', borderRadius: 6, border: `1px solid ${C.border}`, background: C.surface2, color: C.text, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>⬇ JSON 백업</button>
          <label style={{ padding: '7px 14px', borderRadius: 6, border: `1px solid ${C.border}`, background: C.surface2, color: C.text, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
            ⬆ JSON 불러오기
            <input type="file" accept=".json" style={{ display: 'none' }}
              onChange={async (e) => {
                const r = await importBackup(e.target.files?.[0]);
                show(r.ok ? `${r.count}개 프로젝트 가져옴` : `실패: ${r.msg}`);
                e.target.value = '';
              }} />
          </label>
          <button onClick={() => { if (window.confirm('모든 스냅샷을 삭제할까요? 현재 데이터는 그대로 유지됩니다.')) { clearAllSnapshots(); show('스냅샷 비움'); } }}
            style={{ padding: '7px 14px', borderRadius: 6, border: `1px solid ${C.red}44`, background: C.redBg, color: C.red, fontSize: 12, fontWeight: 600, cursor: 'pointer', marginLeft: 'auto' }}>🗑 스냅샷 비우기</button>
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: '8px 16px' }}>
          {snapshots.length === 0 ? (
            <div style={{ padding: '40px 20px', textAlign: 'center', color: C.dim, fontSize: 13 }}>
              아직 스냅샷이 없습니다.<br/><br/>
              <span style={{ fontSize: 11 }}>2분 후 첫 자동 스냅샷이 생성되거나, 위 <strong>📸 지금 스냅샷</strong> 버튼을 누르세요.</span>
            </div>
          ) : snapshots.map(s => (
            <div key={s.ts}
              style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px', borderBottom: `1px solid ${C.border}` }}>
              <div style={{ width: 8, height: 8, borderRadius: '50%', background: s.label === '자동' ? C.blue : (s.label.includes('직전') ? C.red : C.gold), flexShrink: 0 }} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, color: C.text, fontWeight: 700 }}>{fmtTs(s.ts)} <span style={{ fontSize: 10, padding: '2px 6px', borderRadius: 3, background: C.surface2, color: C.dim, marginLeft: 6, fontWeight: 600 }}>{s.label}</span></div>
                <div style={{ fontSize: 10, color: C.dim, marginTop: 2 }}>{projectsSummary(s.projects)}</div>
              </div>
              <button onClick={() => { if (window.confirm(`${fmtTs(s.ts)} 시점으로 복원할까요? 현재 상태는 "복원 전 자동" 스냅샷으로 보관됩니다.`)) { restoreSnapshot(s.ts); show('복원 완료'); onClose(); } }}
                style={{ padding: '6px 12px', borderRadius: 4, border: `1px solid ${C.gold}44`, background: C.goldBg, color: C.gold, fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>↻ 복원</button>
              <button onClick={() => { if (window.confirm('이 스냅샷을 삭제할까요?')) deleteSnapshot(s.ts); }}
                style={{ background: 'transparent', border: 'none', color: C.dim2, cursor: 'pointer', fontSize: 14 }}>×</button>
            </div>
          ))}
        </div>

        <div style={{ padding: '12px 24px', borderTop: `1px solid ${C.border}`, fontSize: 11, color: C.dim, background: C.surface2 }}>
          💡 <strong style={{ color: C.text }}>안전 권장:</strong> 중요한 작업 후 <strong style={{ color: C.gold }}>⬇ JSON 백업</strong> 버튼으로 OneDrive·Dropbox 폴더에 저장해두세요. 브라우저 데이터 초기화에 대비 가능.
        </div>

        {toast && (
          <div style={{ position: 'fixed', bottom: 40, left: '50%', transform: 'translateX(-50%)', background: C.surface2, border: `1px solid ${C.gold}66`, borderRadius: 8, padding: '10px 16px', fontSize: 12, color: C.text, fontWeight: 700, zIndex: 999999 }}>{toast}</div>
        )}
      </div>
    </div>,
    document.body
  );
}
